/**
 * MissionModalController - Handles mission planning modal UI
 * Extracted from main.ts for better organization
 */

import { GameState } from '../core/GameState';
import { AsteroidInfo, getTypesForResource, estimateAsteroidResources, calculateAsteroidMatches } from '../data/AsteroidData';
import { formatCurrency, formatDistance, formatDiameter } from '../utils/Formatters';
import { DEFAULT_CONTRACT_COUNT } from '../utils/Constants';
import {
  LAUNCH_PROVIDERS,
  CREW_TYPES,
  LaunchProvider,
  CrewType,
  Mission,
  MissionStatus,
  MissionPhase,
  Contract,
  generateContracts,
  calculateMissionDuration,
  ALL_DELIVERY_DESTINATIONS,
  calculatePhaseDuration,
  generateMissionId,
  generateCaptainName,
  estimateResourceYield,
} from '../core/MissionTypes';
import { getCurrentLevel } from '../data/HelpContent';
import { hasAnyDepots, getTotalStorageCapacityNew, getTotalStoredTonsNew } from '../data/StorageDepots';
import { NewsType } from './TickerManager';
import { AssetManager } from '../core/AssetManager';
import { OwnableAsset } from '../data/OwnableAssets';

/** Callback interface for modal events */
export interface MissionModalCallbacks {
  onAddNewsItem: (text: string, type: NewsType) => void;
  onUpdateActiveMissionsList: () => void;
  getAsteroidDistanceInfo: (asteroid: AsteroidInfo) => { current: number; min: number; max: number; isNear: boolean };
  isAsteroidAvailable: (asteroidId: string) => boolean;
  getSolarSystemAngles: (asteroidId: string) => { asteroidAngle: number | null | undefined; earthAngle: number | null | undefined };
  onRefreshAsteroids?: () => void;
  getAllAsteroids?: () => AsteroidInfo[];  // Get all asteroids (not just visible)
  getAssetManager?: () => AssetManager;    // Get asset manager for owned vehicles
}

/** Wikipedia links for asteroid spectral types */
const ASTEROID_TYPE_WIKI_LINKS: Record<string, string> = {
  'C': 'https://en.wikipedia.org/wiki/C-type_asteroid',
  'S': 'https://en.wikipedia.org/wiki/S-type_asteroid',
  'M': 'https://en.wikipedia.org/wiki/M-type_asteroid',
  'Q': 'https://en.wikipedia.org/wiki/Q-type_asteroid',
  'V': 'https://en.wikipedia.org/wiki/V-type_asteroid',
  'E': 'https://en.wikipedia.org/wiki/E-type_asteroid',
  'D': 'https://en.wikipedia.org/wiki/D-type_asteroid',
  'P': 'https://en.wikipedia.org/wiki/P-type_asteroid',
  'X': 'https://en.wikipedia.org/wiki/X-type_asteroid',
};

export class MissionModalController {
  private gameState: GameState;
  private asteroidData: Map<string, AsteroidInfo>;
  private callbacks: MissionModalCallbacks;
  
  // Modal state
  private selectedProvider: LaunchProvider | null = null;
  private selectedCrew: CrewType | null = null;
  private currentMissionAsteroid: AsteroidInfo | null = null;
  private availableContracts: Contract[] = [];
  private selectedContract: Contract | null = null;
  private hadPreselectedTarget = false;
  private isContractlessMission = false;
  
  // Owned vehicle selection (alternative to vendor)
  private selectedOwnedVehicle: OwnableAsset | null = null;
  private ownedVehicleFuelCost: number = 0;

  // Owned crew selection (alternative to renting a crew)
  private selectedOwnedCrew: OwnableAsset | null = null;
  private ownedCrewCost: number = 0;
  
  // Cache of asteroids shown in target selection (may not be in asteroidData)
  private targetSelectionCache: Map<string, AsteroidInfo> = new Map();
  
  constructor(
    gameState: GameState,
    asteroidData: Map<string, AsteroidInfo>,
    callbacks: MissionModalCallbacks
  ) {
    this.gameState = gameState;
    this.asteroidData = asteroidData;
    this.callbacks = callbacks;
  }
  
  /** Update asteroid data reference (for when new asteroids are loaded) */
  setAsteroidData(data: Map<string, AsteroidInfo>): void {
    this.asteroidData = data;
  }
  
  /** Setup event listeners for modal navigation */
  setup(): void {
    // Close buttons
    document.getElementById('modal-close')?.addEventListener('click', () => this.close());
    document.getElementById('btn-cancel')?.addEventListener('click', () => this.close());

    // Navigation buttons
    document.getElementById('btn-to-provider')?.addEventListener('click', () => {
      this.showStep('step-provider');
    });
    document.getElementById('btn-back-target')?.addEventListener('click', () => {
      // Go back to the appropriate target step
      if (this.hadPreselectedTarget) {
        this.showStep('step-target');
      } else {
        this.showStep('step-target-select');
      }
    });
    document.getElementById('btn-to-crew')?.addEventListener('click', () => {
      this.showStep('step-crew');
    });
    document.getElementById('btn-back-provider')?.addEventListener('click', () => {
      this.showStep('step-provider');
    });
    document.getElementById('btn-to-summary')?.addEventListener('click', () => {
      this.populateMissionSummary();
      this.showStep('step-summary');
    });
    document.getElementById('btn-back-crew')?.addEventListener('click', () => {
      this.showStep('step-crew');
    });
    document.getElementById('btn-launch')?.addEventListener('click', () => {
      this.launchMission();
    });

    // Contract flow buttons
    document.getElementById('btn-skip-contract')?.addEventListener('click', () => {
      this.selectedContract = null;
      this.populateTargetSuggestions();
      this.showStep('step-target-select');
    });
    document.getElementById('btn-back-contract')?.addEventListener('click', () => {
      this.showStep('step-contract');
    });
    document.getElementById('btn-manual-select')?.addEventListener('click', () => {
      // Close modal, let user select from map
      this.close();
      this.callbacks.onAddNewsItem('Select an asteroid from the map, then click Plan Mission', 'market');
    });
    document.getElementById('btn-to-provider-from-target')?.addEventListener('click', () => {
      if (this.currentMissionAsteroid) {
        this.showStep('step-provider');
      }
    });
    document.getElementById('btn-back-to-contract')?.addEventListener('click', () => {
      if (this.hadPreselectedTarget) {
        this.close();
      } else {
        this.showStep('step-contract');
      }
    });

    // Close on overlay click
    document.getElementById('mission-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.close();
      }
    });
  }
  
  /** Start mission planning flow */
  startPlanning(): void {
    const selectedId = this.gameState.data.selectedAsteroidId;
    
    // Reset state
    this.selectedProvider = null;
    this.selectedCrew = null;
    this.selectedOwnedVehicle = null;
    this.ownedVehicleFuelCost = 0;
    this.selectedOwnedCrew = null;
    this.ownedCrewCost = 0;
    this.selectedContract = null;
    this.availableContracts = generateContracts(DEFAULT_CONTRACT_COUNT, this.gameState.data.market);
    
    if (selectedId) {
      // Easter egg: Sun mission
      if (selectedId === 'sun') {
        // Create fake asteroid entry for the Sun
        this.currentMissionAsteroid = {
          id: 'sun',
          name: 'The Sun',
          fullName: 'Sol (The Sun)',
          semiMajorAxis: 0.01, // Very close
          eccentricity: 0,
          inclination: 0,
          diameter: 1392700, // km
          mass: 1.989e30, // kg
          albedo: null,
          taxonomicClass: 'G', // G-type star, lol
          absoluteMagnitude: -26.74,
        };
        this.hadPreselectedTarget = true;
        this.callbacks.onAddNewsItem('🌞 Planning a mission to the Sun? Bold strategy...', 'flavor');
        this.open(true);
        return;
      }
      
      // Check if this asteroid has already been mined - if so, don't use it
      const isMined = this.gameState.data.minedAsteroids.includes(selectedId);
      if (!isMined) {
        // If an asteroid is already selected and not mined, use it
        const asteroid = this.asteroidData.get(selectedId);
        if (asteroid) {
          this.currentMissionAsteroid = asteroid;
          this.hadPreselectedTarget = true;
          this.open(true);
          return;
        }
      }
    }
    
    // No asteroid selected (or it was mined) - start from contract selection
    this.currentMissionAsteroid = null;
    this.hadPreselectedTarget = false;
    this.open(false);
  }
  
  /** Open the mission modal */
  open(hasPreselectedTarget: boolean): void {
    const modal = document.getElementById('mission-modal');
    if (!modal) return;

    // Populate options
    this.populateContractOptions();
    this.populateProviderOptions();
    this.populateCrewOptions();

    if (hasPreselectedTarget && this.currentMissionAsteroid) {
      // Start at target confirmation
      this.populateTargetSummary();
      this.showStep('step-target');
    } else {
      // Start at contract selection
      this.showStep('step-contract');
    }

    // Show modal
    modal.classList.add('visible');
  }
  
  /** Close the mission modal and reset state */
  close(): void {
    const modal = document.getElementById('mission-modal');
    if (modal) modal.classList.remove('visible');
    this.currentMissionAsteroid = null;
    this.selectedProvider = null;
    this.selectedCrew = null;
    this.selectedOwnedVehicle = null;
    this.ownedVehicleFuelCost = 0;
    this.selectedOwnedCrew = null;
    this.ownedCrewCost = 0;
    this.selectedContract = null;
    this.hadPreselectedTarget = false;
    this.isContractlessMission = false;
    // Clear selected asteroid so next "Plan a Mission" starts fresh
    this.gameState.update({ selectedAsteroidId: null });
  }
  
  /** Show a specific modal step */
  private showStep(stepId: string): void {
    document.querySelectorAll('.modal-step').forEach(el => el.classList.remove('active'));
    const step = document.getElementById(stepId);
    if (step) step.classList.add('active');
  }
  
  /** Populate contract selection options */
  private populateContractOptions(): void {
    const container = document.getElementById('contract-options');
    if (!container) return;

    const level = getCurrentLevel(this.gameState.data.missionsCompleted);
    const hasStorage = hasAnyDepots(this.gameState.data.storage);
    const canSpecFreeMine = level.id >= 3 && hasStorage;

    // Build contract cards
    let html = this.availableContracts.map(contract => {
      const resourceName = contract.resourceType.replace('_', ' ').toUpperCase();
      return `
        <div class="contract-card" data-contract-id="${contract.id}">
          <div class="vendor-name">${contract.vendorName}</div>
          <div class="resource-name">${resourceName}</div>
          <div class="contract-details">
            <div class="contract-detail">
              <span class="label">Quantity:</span>
              <span class="value">${contract.quantityTons.toLocaleString()} tons</span>
            </div>
            <div class="contract-detail">
              <span class="label">Price/ton:</span>
              <span class="value">${formatCurrency(contract.pricePerTon)}</span>
            </div>
            <div class="contract-detail">
              <span class="label">Deadline:</span>
              <span class="value">${contract.deadline} days</span>
            </div>
          </div>
          <div class="total-value">
            <span class="label">Total Value:</span>
            <span class="value">${formatCurrency(contract.totalValue)}</span>
          </div>
        </div>
      `;
    }).join('');

    // Add spec-free mining option if available
    if (canSpecFreeMine) {
      const totalCapacity = getTotalStorageCapacityNew(this.gameState.data.storage);
      const usedCapacity = getTotalStoredTonsNew(this.gameState.data.storage);
      const remainingCapacity = totalCapacity - usedCapacity;
      const depotCount = Object.values(this.gameState.data.storage.depots).reduce((sum, d) => sum + d.count, 0);
      
      html += `
        <div class="contract-card spec-free-card" data-spec-free="true" style="border-color: #4ade80; background: rgba(74, 222, 128, 0.1);">
          <div class="vendor-name" style="color: #4ade80;">⛏️ Spec-Free Mining</div>
          <div class="resource-name">MINE FOR YOURSELF</div>
          <div class="contract-details">
            <div class="contract-detail">
              <span class="label">Depots:</span>
              <span class="value">${depotCount} (${totalCapacity.toLocaleString()} tons total)</span>
            </div>
            <div class="contract-detail">
              <span class="label">Available:</span>
              <span class="value">${remainingCapacity.toLocaleString()} tons</span>
            </div>
            <div class="contract-detail">
              <span class="label">Overflow:</span>
              <span class="value">Sold at market price</span>
            </div>
          </div>
          <div class="total-value">
            <span class="label">Strategy:</span>
            <span class="value" style="font-size: 11px;">Store resources, sell when prices are high</span>
          </div>
        </div>
      `;
    } else if (level.id >= 3) {
      // Show locked spec-free option
      html += `
        <div class="contract-card" style="opacity: 0.5; cursor: not-allowed;">
          <div class="vendor-name" style="color: #888;">⛏️ Spec-Free Mining</div>
          <div class="resource-name" style="color: #888;">REQUIRES STORAGE</div>
          <div style="color: #666; font-size: 11px; padding: 10px 0;">
            Purchase a storage depot to unlock spec-free mining.
          </div>
        </div>
      `;
    }

    container.innerHTML = html;

    // Add click handlers
    container.querySelectorAll('.contract-card').forEach(card => {
      card.addEventListener('click', () => {
        // Skip locked cards
        if ((card as HTMLElement).style.cursor === 'not-allowed') return;
        
        container.querySelectorAll('.contract-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        
        const isSpecFree = (card as HTMLElement).dataset.specFree === 'true';
        
        if (isSpecFree) {
          this.selectedContract = null;
          this.isContractlessMission = true;
          this.populateTargetSuggestions();
          this.showStep('step-target-select');
        } else {
          const contractId = (card as HTMLElement).dataset.contractId;
          this.selectedContract = this.availableContracts.find(c => c.id === contractId) || null;
          this.isContractlessMission = false;
          
          // Move to target selection with this contract
          if (this.selectedContract) {
            this.populateTargetSuggestions();
            this.showStep('step-target-select');
          }
        }
      });
    });
  }
  
  /** Populate asteroid target suggestions */
  private populateTargetSuggestions(): void {
    const container = document.getElementById('target-options');
    const hint = document.getElementById('target-select-hint');
    if (!container) return;

    // Get best asteroid types for the selected resource
    const goodTypes = this.selectedContract 
      ? getTypesForResource(this.selectedContract.resourceType)
      : [];
    
    const resourceName = this.selectedContract?.resourceType.replace('_', ' ') || 'resources';
    
    if (hint) {
      if (this.selectedContract) {
        const typeLinks = goodTypes.slice(0, 3).map(type => {
          const url = ASTEROID_TYPE_WIKI_LINKS[type];
          return url 
            ? `<a href="${url}" target="_blank" class="type-link">${type}</a>` 
            : type;
        }).join(', ');
        hint.innerHTML = `Showing asteroids likely to contain ${resourceName}. Best types: ${typeLinks}-class.`;
      } else {
        hint.textContent = 'Select an asteroid to mine.';
      }
    }

    // Score and sort asteroids by suitability, excluding unavailable asteroids
    // Use all asteroids from static data if available, otherwise fall back to visible asteroids
    const allAsteroids = this.callbacks.getAllAsteroids?.() || Array.from(this.asteroidData.values());
    const availableAsteroids = allAsteroids
      .filter(asteroid => this.callbacks.isAsteroidAvailable(asteroid.id));
    
    // Debug: Log asteroid counts when empty
    if (availableAsteroids.length === 0) {
      console.warn('[MissionModal] No available asteroids for target selection', {
        totalAsteroids: allAsteroids.length,
        availableCount: availableAsteroids.length,
        resourceType: this.selectedContract?.resourceType,
      });
    }
    
    // Use centralized match calculation if we have a contract
    let scoredAsteroids: { asteroid: AsteroidInfo; matchPercent: number }[];
    
    if (this.selectedContract) {
      const matchResults = calculateAsteroidMatches(availableAsteroids, this.selectedContract.resourceType);
      scoredAsteroids = matchResults.map(r => ({ asteroid: r.asteroid, matchPercent: r.matchPercent }));
    } else {
      // No contract - just show all asteroids with neutral match
      scoredAsteroids = availableAsteroids.map(asteroid => ({ asteroid, matchPercent: 50 }));
    }

    // Show top 8 suggestions
    const topSuggestions = scoredAsteroids.slice(0, 8);

    // Handle empty state
    if (topSuggestions.length === 0) {
      container.innerHTML = `
        <div class="empty-state" style="text-align: center; padding: 20px; color: #888;">
          <p>No suitable asteroids found.</p>
          <p style="font-size: 0.9em; margin-top: 10px;">
            ${allAsteroids.length === 0 
              ? 'Waiting for asteroids to load...' 
              : 'All known asteroids are currently unavailable (mined or claimed).'}
          </p>
          <button id="btn-retry-asteroids" class="btn" style="margin-top: 15px;">Refresh Asteroid Data</button>
        </div>
      `;
      
      // Add retry button handler
      document.getElementById('btn-retry-asteroids')?.addEventListener('click', () => {
        this.callbacks.onRefreshAsteroids?.();
        setTimeout(() => this.populateTargetSuggestions(), 1000);
      });
      return;
    }

    // Cache the asteroids we're showing (they may not be in visible asteroidData)
    this.targetSelectionCache.clear();
    for (const { asteroid } of topSuggestions) {
      this.targetSelectionCache.set(asteroid.id, asteroid);
    }

    container.innerHTML = topSuggestions.map(({ asteroid, matchPercent }) => {
      const distanceInfo = this.callbacks.getAsteroidDistanceInfo(asteroid);
      const diameterStr = asteroid.diameter ? formatDiameter(asteroid.diameter) : '?';
      
      return `
        <div class="target-card" data-asteroid-id="${asteroid.id}">
          <div class="target-info">
            <div class="target-name">${asteroid.name}</div>
            <div class="target-details">${asteroid.taxonomicClass}-class • ${diameterStr} • ${formatDistance(distanceInfo.current)} (${distanceInfo.isNear ? 'near' : 'far'})</div>
          </div>
          <div class="target-match">
            <div class="match-label">Match</div>
            <div class="match-value">${matchPercent}%</div>
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers
    container.querySelectorAll('.target-card').forEach(card => {
      card.addEventListener('click', () => {
        container.querySelectorAll('.target-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        const asteroidId = (card as HTMLElement).dataset.asteroidId;
        // Look up from cache first, then visible asteroids
        this.currentMissionAsteroid = this.targetSelectionCache.get(asteroidId!) 
          || this.asteroidData.get(asteroidId!) 
          || null;
        
        const nextBtn = document.getElementById('btn-to-provider-from-target') as HTMLButtonElement;
        if (nextBtn) nextBtn.disabled = false;
      });
    });

    // Reset next button
    const nextBtn = document.getElementById('btn-to-provider-from-target') as HTMLButtonElement;
    if (nextBtn) nextBtn.disabled = true;
  }
  
  /** Populate target summary for pre-selected asteroid */
  private populateTargetSummary(): void {
    const container = document.getElementById('target-summary');
    if (!container || !this.currentMissionAsteroid) return;

    const asteroid = this.currentMissionAsteroid;
    const distanceInfo = this.callbacks.getAsteroidDistanceInfo(asteroid);
    const distanceFromEarth = distanceInfo.current;
    const techEffects = this.gameState.getTechEffects();
    const duration = calculateMissionDuration(distanceFromEarth, techEffects.travelTimeModifier);
    const diameterStr = asteroid.diameter ? formatDiameter(asteroid.diameter) : 'Unknown';
    
    // Show estimated resources
    const resources = estimateAsteroidResources(asteroid.taxonomicClass, asteroid.diameter, null);
    const topResources = Object.entries(resources)
      .filter(([_, amount]) => amount > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    container.innerHTML = `
      <h4 style="color: var(--hud-cyan); margin: 0 0 12px 0;">${asteroid.name}</h4>
      <div class="summary-row">
        <span class="label">Type:</span>
        <span class="value">${asteroid.taxonomicClass}-class Asteroid</span>
      </div>
      <div class="summary-row">
        <span class="label">Distance:</span>
        <span class="value">${formatDistance(distanceFromEarth)} from Earth (${distanceInfo.isNear ? 'near approach' : 'far approach'})</span>
      </div>
      <div class="summary-row">
        <span class="label">Range:</span>
        <span class="value">${formatDistance(distanceInfo.min)} - ${formatDistance(distanceInfo.max)}</span>
      </div>
      <div class="summary-row">
        <span class="label">Diameter:</span>
        <span class="value">${diameterStr}</span>
      </div>
      <div class="summary-row">
        <span class="label">Est. Trip Duration:</span>
        <span class="value">${duration.total.toFixed(1)} days</span>
      </div>
      <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(84, 230, 255, 0.2);">
        <div style="color: var(--text-muted); font-size: 11px; margin-bottom: 6px;">LIKELY RESOURCES:</div>
        ${topResources.map(([resource, amount]) => `
          <div class="summary-row">
            <span class="label">${resource.replace('_', ' ')}:</span>
            <span class="value">${(amount / 1000).toFixed(0)}t est.</span>
          </div>
        `).join('')}
      </div>
    `;
  }
  
  /** Populate launch provider options */
  private populateProviderOptions(): void {
    const container = document.getElementById('provider-options');
    if (!container) return;

    // Reset owned vehicle selection
    this.selectedOwnedVehicle = null;
    this.ownedVehicleFuelCost = 0;
    
    // Get owned vehicle options from AssetManager
    const assetManager = this.callbacks.getAssetManager?.();
    
    // Calculate approximate vendor costs for fuel pricing (costPerKg * typical payload ~15000kg)
    const TYPICAL_PAYLOAD_KG = 15000;
    const vendorCosts = LAUNCH_PROVIDERS.map(p => p.costPerKg * TYPICAL_PAYLOAD_KG);
    const ownedVehicles = assetManager?.getVehicleOptionsForMission(vendorCosts) || [];
    
    // Build HTML - owned vehicles at top
    let html = '';
    
    // Owned vehicles section (if any available)
    if (ownedVehicles.length > 0) {
      const availableVehicles = ownedVehicles.filter(v => v.availableCount > 0);
      const inUseVehicles = ownedVehicles.filter(v => v.availableCount === 0);
      
      html += '<div class="owned-vehicles-section">';
      html += '<h4 class="section-label" style="color: var(--hud-cyan); margin-bottom: 10px;">🚀 Your Vehicles</h4>';
      
      // Available vehicles
      for (const vehicle of availableVehicles) {
        html += `
          <div class="option-card owned-vehicle" data-owned-vehicle-id="${vehicle.asset.id}" data-fuel-cost="${vehicle.cost}">
            <div class="owned-badge" style="position: absolute; top: 5px; right: 5px; background: var(--hud-cyan); color: #000; padding: 2px 6px; border-radius: 3px; font-size: 10px;">OWNED</div>
            <h4>${vehicle.asset.name}</h4>
            <div class="description">${vehicle.tagline}</div>
            <div class="option-stats">
              <div class="option-stat">
                <span class="stat-label">Fuel Cost:</span>
                <span class="stat-value highlight">${formatCurrency(vehicle.cost)}</span>
              </div>
              <div class="option-stat">
                <span class="stat-label">Payload:</span>
                <span class="stat-value">${((vehicle.asset.payloadCapacity || 10000) / 1000).toFixed(0)}t</span>
              </div>
              <div class="option-stat">
                <span class="stat-label">Reliability:</span>
                <span class="stat-value">${((vehicle.asset.reliability || 0.95) * 100).toFixed(1)}%</span>
              </div>
              <div class="option-stat">
                <span class="stat-label">Available:</span>
                <span class="stat-value">${vehicle.availableCount}/${vehicle.totalOwned}</span>
              </div>
            </div>
          </div>
        `;
      }
      
      // In-use vehicles (disabled)
      for (const vehicle of inUseVehicles) {
        html += `
          <div class="option-card owned-vehicle disabled" style="opacity: 0.5; cursor: not-allowed;">
            <div class="owned-badge" style="position: absolute; top: 5px; right: 5px; background: #666; color: #fff; padding: 2px 6px; border-radius: 3px; font-size: 10px;">IN USE</div>
            <h4>${vehicle.asset.name}</h4>
            <div class="description">${vehicle.tagline}</div>
            <div class="option-stats">
              <div class="option-stat">
                <span class="stat-label">Status:</span>
                <span class="stat-value">All ${vehicle.totalOwned} on missions</span>
              </div>
            </div>
          </div>
        `;
      }
      
      html += '</div>';
      html += '<h4 class="section-label" style="color: #888; margin: 15px 0 10px 0; border-top: 1px solid #333; padding-top: 15px;">— Or Hire a Vendor —</h4>';
    }
    
    // Standard vendors
    html += LAUNCH_PROVIDERS.map(provider => `
      <div class="option-card" data-provider-id="${provider.id}">
        <h4>${provider.name}</h4>
        <div class="description">${provider.description}</div>
        <div class="option-stats">
          <div class="option-stat">
            <span class="stat-label">Cost/kg:</span>
            <span class="stat-value">${formatCurrency(provider.costPerKg)}</span>
          </div>
          <div class="option-stat">
            <span class="stat-label">Payload:</span>
            <span class="stat-value">${(provider.payloadCapacity / 1000).toFixed(0)}t</span>
          </div>
          <div class="option-stat">
            <span class="stat-label">Reliability:</span>
            <span class="stat-value">${(provider.reliability * 100).toFixed(1)}%</span>
          </div>
          <div class="option-stat">
            <span class="stat-label">Frequency:</span>
            <span class="stat-value">${provider.launchFrequency}</span>
          </div>
        </div>
      </div>
    `).join('');
    
    container.innerHTML = html;

    // Add click handlers for owned vehicles
    container.querySelectorAll('.owned-vehicle:not(.disabled)').forEach(card => {
      card.addEventListener('click', () => {
        container.querySelectorAll('.option-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        
        const vehicleId = (card as HTMLElement).dataset.ownedVehicleId;
        const fuelCost = parseInt((card as HTMLElement).dataset.fuelCost || '0', 10);
        
        const vehicleOption = ownedVehicles.find(v => v.asset.id === vehicleId);
        this.selectedOwnedVehicle = vehicleOption?.asset || null;
        this.ownedVehicleFuelCost = fuelCost;
        this.selectedProvider = null; // Clear vendor selection
        
        const nextBtn = document.getElementById('btn-to-crew') as HTMLButtonElement;
        if (nextBtn) nextBtn.disabled = false;
      });
    });

    // Add click handlers for vendors
    container.querySelectorAll('.option-card[data-provider-id]').forEach(card => {
      card.addEventListener('click', () => {
        container.querySelectorAll('.option-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        
        const providerId = (card as HTMLElement).dataset.providerId;
        this.selectedProvider = LAUNCH_PROVIDERS.find(p => p.id === providerId) || null;
        this.selectedOwnedVehicle = null; // Clear owned vehicle selection
        this.ownedVehicleFuelCost = 0;
        
        const nextBtn = document.getElementById('btn-to-crew') as HTMLButtonElement;
        if (nextBtn) nextBtn.disabled = false;
      });
    });
  }
  
  /** Convert a purchased crew asset into the mission crew shape. */
  private createCrewTypeFromOwnedAsset(asset: OwnableAsset): CrewType {
    return {
      id: asset.id,
      name: asset.name,
      description: asset.description,
      dailyCost: 0,
      miningEfficiency: asset.efficiency || 1,
      reliability: asset.reliability || 0.9,
      requiredPayload: asset.requiredPayload || 0,
    };
  }

  /** Populate crew type options */
  private populateCrewOptions(): void {
    const container = document.getElementById('crew-options');
    if (!container) return;

    this.selectedOwnedCrew = null;
    this.ownedCrewCost = 0;

    const assetManager = this.callbacks.getAssetManager?.();
    const ownedCrews = assetManager?.getCrewOptionsForMission() || [];

    let html = '';

    if (ownedCrews.length > 0) {
      const availableCrews = ownedCrews.filter(crew => crew.availableCount > 0);
      const inUseCrews = ownedCrews.filter(crew => crew.availableCount === 0);

      html += '<div class="owned-crews-section">';
      html += '<h4 class="section-label" style="color: var(--hud-cyan); margin-bottom: 10px;">🤖 Your Crews</h4>';

      for (const crew of availableCrews) {
        html += `
          <div class="option-card owned-crew" data-owned-crew-id="${crew.asset.id}" data-crew-cost="${crew.cost}">
            <div class="owned-badge" style="position: absolute; top: 5px; right: 5px; background: var(--hud-cyan); color: #000; padding: 2px 6px; border-radius: 3px; font-size: 10px;">OWNED</div>
            <h4>${crew.asset.name}</h4>
            <div class="description">${crew.tagline}</div>
            <div class="option-stats">
              <div class="option-stat">
                <span class="stat-label">Crew Cost:</span>
                <span class="stat-value highlight">${formatCurrency(crew.cost)}</span>
              </div>
              <div class="option-stat">
                <span class="stat-label">Efficiency:</span>
                <span class="stat-value">${((crew.asset.efficiency || 1) * 100).toFixed(0)}%</span>
              </div>
              <div class="option-stat">
                <span class="stat-label">Reliability:</span>
                <span class="stat-value">${((crew.asset.reliability || 0.9) * 100).toFixed(0)}%</span>
              </div>
              <div class="option-stat">
                <span class="stat-label">Available:</span>
                <span class="stat-value">${crew.availableCount}/${crew.totalOwned}</span>
              </div>
            </div>
          </div>
        `;
      }

      for (const crew of inUseCrews) {
        html += `
          <div class="option-card owned-crew disabled" style="opacity: 0.5; cursor: not-allowed;">
            <div class="owned-badge" style="position: absolute; top: 5px; right: 5px; background: #666; color: #fff; padding: 2px 6px; border-radius: 3px; font-size: 10px;">IN USE</div>
            <h4>${crew.asset.name}</h4>
            <div class="description">${crew.tagline}</div>
            <div class="option-stats">
              <div class="option-stat">
                <span class="stat-label">Status:</span>
                <span class="stat-value">All ${crew.totalOwned} on missions</span>
              </div>
            </div>
          </div>
        `;
      }

      html += '</div>';
      html += '<h4 class="section-label" style="color: #888; margin: 15px 0 10px 0; border-top: 1px solid #333; padding-top: 15px;">— Or Hire a Crew —</h4>';
    }

    html += CREW_TYPES.map(crew => `
      <div class="option-card" data-crew-id="${crew.id}">
        <h4>${crew.name}</h4>
        <div class="description">${crew.description}</div>
        <div class="option-stats">
          <div class="option-stat">
            <span class="stat-label">Daily Cost:</span>
            <span class="stat-value">${formatCurrency(crew.dailyCost)}</span>
          </div>
          <div class="option-stat">
            <span class="stat-label">Efficiency:</span>
            <span class="stat-value">${(crew.miningEfficiency * 100).toFixed(0)}%</span>
          </div>
          <div class="option-stat">
            <span class="stat-label">Reliability:</span>
            <span class="stat-value">${(crew.reliability * 100).toFixed(0)}%</span>
          </div>
          <div class="option-stat">
            <span class="stat-label">Payload Req:</span>
            <span class="stat-value">${(crew.requiredPayload / 1000).toFixed(1)}t</span>
          </div>
        </div>
      </div>
    `).join('');

    container.innerHTML = html;

    // Add click handlers for owned crews
    container.querySelectorAll('.owned-crew:not(.disabled)').forEach(card => {
      card.addEventListener('click', () => {
        container.querySelectorAll('.option-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');

        const crewId = (card as HTMLElement).dataset.ownedCrewId;
        const crewCost = parseInt((card as HTMLElement).dataset.crewCost || '0', 10);
        const crewOption = ownedCrews.find(c => c.asset.id === crewId);

        this.selectedOwnedCrew = crewOption?.asset || null;
        this.ownedCrewCost = crewCost;
        this.selectedCrew = this.selectedOwnedCrew
          ? this.createCrewTypeFromOwnedAsset(this.selectedOwnedCrew)
          : null;

        const nextBtn = document.getElementById('btn-to-summary') as HTMLButtonElement;
        if (nextBtn) nextBtn.disabled = false;
      });
    });

    // Add click handlers for rentable crews
    container.querySelectorAll('.option-card[data-crew-id]').forEach(card => {
      card.addEventListener('click', () => {
        container.querySelectorAll('.option-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        const crewId = (card as HTMLElement).dataset.crewId;
        this.selectedCrew = CREW_TYPES.find(c => c.id === crewId) || null;
        this.selectedOwnedCrew = null;
        this.ownedCrewCost = 0;
        
        const nextBtn = document.getElementById('btn-to-summary') as HTMLButtonElement;
        if (nextBtn) nextBtn.disabled = false;
      });
    });
  }
  
  /** Populate final mission summary before launch */
  private populateMissionSummary(): void {
    const container = document.getElementById('mission-summary');
    // Allow either selectedProvider OR selectedOwnedVehicle
    if (!container || !this.currentMissionAsteroid || !this.selectedCrew) return;
    if (!this.selectedProvider && !this.selectedOwnedVehicle) return;

    const asteroid = this.currentMissionAsteroid;
    const crew = this.selectedCrew;
    const usingOwnedVehicle = !!this.selectedOwnedVehicle;
    const usingOwnedCrew = !!this.selectedOwnedCrew;
    
    const distanceInfo = this.callbacks.getAsteroidDistanceInfo(asteroid);
    const distanceFromEarth = distanceInfo.current;
    const techEffects = this.gameState.getTechEffects();
    const duration = calculateMissionDuration(distanceFromEarth, techEffects.travelTimeModifier);
    
    // Calculate costs based on provider type
    let launchCost: number;
    let providerName: string;
    
    if (usingOwnedVehicle) {
      launchCost = this.ownedVehicleFuelCost;
      providerName = `${this.selectedOwnedVehicle!.name} (Your Vehicle)`;
    } else {
      const provider = this.selectedProvider!;
      launchCost = provider.costPerKg * crew.requiredPayload;
      providerName = provider.name;
    }
    
    const crewCost = usingOwnedCrew
      ? this.ownedCrewCost
      : Math.round(crew.dailyCost * duration.total * (techEffects.crewCostModifier || 1.0));
    const totalCost = launchCost + crewCost;
    
    const baseYield = estimateResourceYield(asteroid.diameter, asteroid.taxonomicClass, crew.miningEfficiency, asteroid.mass);
    const estimatedYield = Math.round(baseYield * (techEffects.yieldModifier || 1.0));
    
    const canAfford = this.gameState.data.balance >= totalCost;
    const fundsClass = canAfford ? '' : 'insufficient-funds';

    container.innerHTML = `
      <div class="summary-section">
        <h4>Target</h4>
        <div class="summary-row">
          <span class="label">Asteroid:</span>
          <span class="value">${asteroid.name}</span>
        </div>
        <div class="summary-row">
          <span class="label">Distance:</span>
          <span class="value">${formatDistance(distanceFromEarth)} (${distanceInfo.isNear ? 'near' : 'far'})</span>
        </div>
      </div>

      <div class="summary-section">
        <h4>Mission Details</h4>
        <div class="summary-row">
          <span class="label">Launch Provider:</span>
          <span class="value">${providerName}</span>
        </div>
        <div class="summary-row">
          <span class="label">Crew:</span>
          <span class="value">${crew.name}${usingOwnedCrew ? ' (Your Crew)' : ''}</span>
        </div>
        <div class="summary-row">
          <span class="label">Total Duration:</span>
          <span class="value">${duration.total.toFixed(1)} days</span>
        </div>
        <div class="summary-row">
          <span class="label">Est. Yield:</span>
          <span class="value">${estimatedYield.toLocaleString()} tons</span>
        </div>
      </div>

      <div class="summary-section">
        <h4>Costs</h4>
        <div class="summary-row">
          <span class="label">${usingOwnedVehicle ? 'Fuel Cost:' : 'Launch Cost:'}</span>
          <span class="value">${formatCurrency(launchCost)}</span>
        </div>
        <div class="summary-row">
          <span class="label">${usingOwnedCrew ? 'Crew Cost:' : `Crew Cost (${duration.total.toFixed(1)} days):`}</span>
          <span class="value">${formatCurrency(crewCost)}</span>
        </div>
        <div class="summary-row total">
          <span class="label">Total Cost:</span>
          <span class="value ${fundsClass}">${formatCurrency(totalCost)}</span>
        </div>
        <div class="summary-row">
          <span class="label">Your Balance:</span>
          <span class="value ${fundsClass}">${formatCurrency(this.gameState.data.balance)}</span>
        </div>
      </div>
    `;

    // Update launch button state
    const launchBtn = document.getElementById('btn-launch') as HTMLButtonElement;
    if (launchBtn) {
      launchBtn.disabled = !canAfford;
      launchBtn.textContent = canAfford ? 'Launch Mission' : 'Insufficient Funds';
    }
  }
  
  /** Create and launch the mission */
  private launchMission(): void {
    // Check we have either a provider or an owned vehicle
    if (!this.currentMissionAsteroid || !this.selectedCrew) return;
    if (!this.selectedProvider && !this.selectedOwnedVehicle) return;

    const asteroid = this.currentMissionAsteroid;
    const crew = this.selectedCrew;
    const usingOwnedVehicle = !!this.selectedOwnedVehicle;
    const usingOwnedCrew = !!this.selectedOwnedCrew;
    
    // Get provider info (either from vendor or construct from owned vehicle)
    let providerInfo: {
      id: string;
      name: string;
      launchCost: number;
      reliability: number;
      launchFrequency: string;
    };
    
    if (usingOwnedVehicle) {
      const vehicle = this.selectedOwnedVehicle!;
      providerInfo = {
        id: `owned_${vehicle.id}`,
        name: vehicle.name,
        launchCost: this.ownedVehicleFuelCost,
        reliability: vehicle.reliability || 0.95,
        launchFrequency: 'On Demand', // Owned vehicles can launch when ready
      };
    } else {
      const provider = this.selectedProvider!;
      const estimatedLaunchCost = provider.costPerKg * crew.requiredPayload;
      providerInfo = {
        id: provider.id,
        name: provider.name,
        launchCost: estimatedLaunchCost,
        reliability: provider.reliability,
        launchFrequency: provider.launchFrequency,
      };
    }
    
    const distanceInfo = this.callbacks.getAsteroidDistanceInfo(asteroid);
    const distanceFromEarth = distanceInfo.current;
    const techEffects = this.gameState.getTechEffects();
    const duration = calculateMissionDuration(distanceFromEarth, techEffects.travelTimeModifier);
    
    const launchCost = usingOwnedVehicle
      ? this.ownedVehicleFuelCost
      : this.selectedProvider!.costPerKg * crew.requiredPayload;
    const crewCost = usingOwnedCrew
      ? this.ownedCrewCost
      : Math.round(crew.dailyCost * duration.total * (techEffects.crewCostModifier || 1.0));
    const totalCost = launchCost + crewCost;

    // Check funds
    if (!this.gameState.subtractMoney(totalCost)) {
      this.callbacks.onAddNewsItem('Insufficient funds for mission!', 'critical');
      return;
    }

    // Generate mission ID once, before any other operations
    const missionId = generateMissionId();

    // If using owned vehicle, assign it in AssetManager
    const assetManager = this.callbacks.getAssetManager?.();
    if (usingOwnedVehicle && assetManager) {
      if (!assetManager.assignToMission(this.selectedOwnedVehicle!.id, missionId)) {
        // Failed to assign - vehicle no longer available
        this.callbacks.onAddNewsItem('Vehicle no longer available!', 'critical');
        this.gameState.addMoney(totalCost); // Refund
        return;
      }
    }

    if (usingOwnedCrew && assetManager) {
      if (!assetManager.assignToMission(this.selectedOwnedCrew!.id, missionId)) {
        // Failed to assign - crew no longer available
        this.callbacks.onAddNewsItem('Crew no longer available!', 'critical');
        if (usingOwnedVehicle) {
          assetManager.releaseFromMission(this.selectedOwnedVehicle!.id, missionId);
        }
        this.gameState.addMoney(totalCost); // Refund
        return;
      }
    }

    // Calculate initial phase duration
    const initialPhase = MissionPhase.CONTRACT_SIGNED;
    const initialPhaseDuration = calculatePhaseDuration(
      initialPhase,
      duration.outbound,
      duration.mining,
      duration.return,
      providerInfo.launchFrequency
    );

    // Calculate total mission duration including all phases
    const contractDuration = initialPhaseDuration;
    const launchDuration = calculatePhaseDuration(MissionPhase.LAUNCH, duration.outbound, duration.mining, duration.return, providerInfo.launchFrequency);
    const deliveryDuration = calculatePhaseDuration(MissionPhase.DELIVERING_PAYLOAD, duration.outbound, duration.mining, duration.return, providerInfo.launchFrequency);
    const totalWithPhases = contractDuration + launchDuration + duration.outbound + duration.mining + duration.return + deliveryDuration;

    // Determine target depot for spec-free mining (use first owned depot)
    const ownedDepotIds = Object.keys(this.gameState.data.storage.depots).filter(
      id => this.gameState.data.storage.depots[id].count > 0
    );
    const targetDepotId = this.isContractlessMission && ownedDepotIds.length > 0
      ? ownedDepotIds[0]
      : undefined;

    // Generate captain name and delivery destination
    const captainName = generateCaptainName();
    const deliveryDestination = ALL_DELIVERY_DESTINATIONS[Math.floor(Math.random() * ALL_DELIVERY_DESTINATIONS.length)];
    
    // Calculate expected payload
    const expectedPayload = estimateResourceYield(asteroid.diameter, asteroid.taxonomicClass, crew.miningEfficiency, asteroid.mass);

    // Create mission
    const mission: Mission = {
      id: missionId,
      asteroidId: asteroid.id,
      asteroidName: asteroid.name,
      providerId: providerInfo.id,
      providerName: providerInfo.name,
      crewId: crew.id,
      crewName: crew.name,
      // Owned vehicle tracking
      ownedVehicleId: usingOwnedVehicle ? this.selectedOwnedVehicle!.id : undefined,
      ownedVehicleName: usingOwnedVehicle ? this.selectedOwnedVehicle!.name : undefined,
      // Owned crew tracking
      ownedCrewId: usingOwnedCrew ? this.selectedOwnedCrew!.id : undefined,
      ownedCrewName: usingOwnedCrew ? this.selectedOwnedCrew!.name : undefined,
      captainName,
      // Contract info
      contractId: this.selectedContract?.id,
      contractVendorName: this.selectedContract?.vendorName,
      contractValue: this.selectedContract?.totalValue,
      contractDeadline: this.selectedContract?.deadline,
      resourceType: this.selectedContract?.resourceType || this.currentMissionAsteroid?.taxonomicClass || 'Unknown',
      isContractlessMission: this.isContractlessMission,
      targetDepotId: targetDepotId,
      // Distance
      distanceAU: distanceFromEarth,
      // Timing
      launchTime: this.gameState.data.gameTime,
      outboundDuration: duration.outbound,
      miningDuration: duration.mining,
      returnDuration: duration.return,
      totalDuration: totalWithPhases,
      launchCost: launchCost,
      crewCost: crewCost,
      totalCost: totalCost,
      status: MissionStatus.OUTBOUND,
      progress: 0,
      // Phase tracking
      currentPhase: initialPhase,
      phaseStartTime: this.gameState.data.gameTime,
      phaseDuration: initialPhaseDuration,
      phaseJustChanged: false,
      providerReliability: providerInfo.reliability,
      crewReliability: crew.reliability,
      // Payload and delivery
      expectedPayload,
      deliveryDestination,
    };

    // Add to game state
    this.gameState.addMission(mission);

    // Update UI
    this.callbacks.onUpdateActiveMissionsList();

    // Close modal and show news
    const vehicleNote = usingOwnedVehicle ? ` using your ${this.selectedOwnedVehicle!.name}` : '';
    const crewNote = usingOwnedCrew ? ` with your ${this.selectedOwnedCrew!.name}` : '';
    const newsType = this.isContractlessMission ? 'Spec-free mining mission' : 'Contract signed';
    this.close();
    this.callbacks.onAddNewsItem(`${newsType} for mission to ${asteroid.name}${vehicleNote}${crewNote}!`, 'important');
  }
}
